import { useEffect, useState } from 'react';
import type { Aircraft } from '../lib/api';
import { fmt } from '../lib/altitude';
import type { LiveStatus, WatchlistEntry } from '../lib/useWatchlist';
import Sparkline from './Sparkline';

const isMobile = () =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;

const STATUS_LABEL: Record<LiveStatus, string> = {
  airborne: 'Airborne',
  ground: 'Boden',
  offline: 'Offline',
};

interface WatchlistPanelProps {
  entries: WatchlistEntry[];
  selectedHex: string | null;
  onSelect: (ac: Aircraft) => void;
  onRemove: (hex: string) => Promise<string | null>;
  onFocus: (ac: Aircraft) => void;
  token: string;
  setToken: (t: string) => void;
}

export default function WatchlistPanel({
  entries,
  selectedHex,
  onSelect,
  onRemove,
  onFocus,
  token,
  setToken,
}: WatchlistPanelProps) {
  // Auf Mobile standardmäßig geschlossen, damit die Karte frei bleibt.
  const [open, setOpen] = useState(() => !isMobile());
  const [error, setError] = useState<string | null>(null);

  // Auf Mobile bei einer Auswahl automatisch schließen (Karte + Sheet zeigen).
  useEffect(() => {
    if (selectedHex && isMobile()) setOpen(false);
  }, [selectedHex]);

  return (
    <div className={`watchlist-panel ${open ? 'open' : ''}`}>
      <button
        className="watchlist-toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="star" aria-hidden="true">★</span>
        Watchlist
        {entries.length > 0 && <span className="count">{entries.length}</span>}
        <span className="chevron" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="watchlist-body">
          {entries.length === 0 && (
            <p className="muted empty">
              Noch leer — Flieger auf der Karte anklicken und mit ★ beobachten.
            </p>
          )}

          {entries.map((e) => (
            <div
              key={e.hex}
              className={`watch-card status-${e.status}`}
              onClick={() => {
                // Auch ohne Live-Daten anklickbar — Detailpanel zeigt dann
                // Stammdaten + Historie (Telemetrie bleibt leer).
                onSelect(e.live ?? { hex: e.hex, flight: e.open_flight?.callsign ?? undefined });
                if (e.live) onFocus(e.live);
              }}
              role="button"
              tabIndex={0}
            >
              <div className="watch-card-head">
                <span className="mono watch-callsign">
                  {e.live?.flight || e.open_flight?.callsign || e.label || e.hex.toUpperCase()}
                </span>
                <span className={`status-chip mono chip-${e.status}`}>{STATUS_LABEL[e.status]}</span>
              </div>
              <div className="watch-card-sub mono">
                {e.hex.toUpperCase()}
                {e.live?.r ? ` · ${e.live.r}` : e.label ? ` · ${e.label}` : ''}
              </div>
              {e.status === 'airborne' && e.live && (
                <div className="watch-card-telemetry mono">
                  {fmt.alt(e.live.alt_baro)} · {fmt.speed(e.live.gs)}
                </div>
              )}
              <Sparkline values={e.recent_durations} />
              <button
                className="watch-remove"
                title="Von Watchlist entfernen"
                onClick={async (ev) => {
                  ev.stopPropagation();
                  setError(await onRemove(e.hex));
                }}
              >
                ✕
              </button>
            </div>
          ))}

          {error && <p className="search-error mono">{error}</p>}

          <label className="token-field">
            <span className="telemetry-label">API-TOKEN</span>
            <input
              className="mono"
              type="password"
              value={token}
              placeholder="für Änderungen nötig"
              onChange={(ev) => setToken(ev.target.value)}
              spellCheck={false}
            />
          </label>
        </div>
      )}
    </div>
  );
}
